import React, { useEffect, useState } from 'react';
import { statsApi, exportApi } from '../lib/api';
import { BarChart3, Download, TrendingUp, Users, CreditCard, Calendar, Tv, Star, Wallet, Gift, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#2AABEE', '#00FF94', '#FFD700', '#FF3333', '#9333EA', '#F97316', '#EC4899', '#06B6D4'];

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState('csv');

  const fetchStats = async () => {
    try {
      const res = await statsApi.get();
      setStats(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки статистики');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleExportSubscriptions = async () => {
    try {
      const res = await exportApi.subscriptions(exportFormat);
      if (exportFormat === 'csv') {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'subscriptions.csv';
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'subscriptions.json';
        a.click();
      }
      toast.success('Экспорт подписок завершен');
    } catch (error) {
      toast.error('Ошибка экспорта');
    }
  };

  const handleExportPayments = async () => {
    try {
      const res = await exportApi.payments(exportFormat);
      if (exportFormat === 'csv') {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'payments.csv';
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'payments.json';
        a.click();
      }
      toast.success('Экспорт платежей завершен');
    } catch (error) {
      toast.error('Ошибка экспорта');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const subscriptionPieData = [
    { name: 'Активные', value: stats?.subscriptions?.active || 0 },
    { name: 'Другие', value: (stats?.subscriptions?.total || 0) - (stats?.subscriptions?.active || 0) },
  ].filter(d => d.value > 0);

  const paymentPieData = [
    { name: 'Подтвержденные', value: stats?.payments?.confirmed || 0 },
    { name: 'Ожидающие', value: stats?.payments?.pending || 0 },
    { name: 'Другие', value: (stats?.payments?.total || 0) - (stats?.payments?.confirmed || 0) - (stats?.payments?.pending || 0) },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="stats-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Статистика</h1>
          <p className="text-zinc-500">Аналитика и отчеты по подпискам</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
          <p className="stat-value">{formatCurrency(stats?.revenue?.total)}</p>
          <p className="stat-label mt-1">Общий доход</p>
        </div>
        <div className="stat-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#2AABEE]/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#2AABEE]" />
            </div>
          </div>
          <p className="stat-value">{formatCurrency(stats?.revenue?.recent_7_days)}</p>
          <p className="stat-label mt-1">За последние 7 дней</p>
        </div>
        <div className="stat-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-400" />
            </div>
          </div>
          <p className="stat-value">{stats?.subscriptions?.total || 0}</p>
          <p className="stat-label mt-1">Всего подписок</p>
        </div>
        <div className="stat-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
          <p className="stat-value">{stats?.payments?.total || 0}</p>
          <p className="stat-label mt-1">Всего платежей</p>
        </div>
      </div>

      {/* Referral summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-3 text-amber-400">
            <Gift className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Реферальные привязки</span>
          </div>
          <p className="stat-value">{stats?.referrals?.bindings || 0}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-3 text-amber-400">
            <Trophy className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Оплаченных рефералов</span>
          </div>
          <p className="stat-value">{stats?.referrals?.paid_referrals || 0}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-3 text-amber-400">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Выручка по рефералам</span>
          </div>
          <p className="stat-value">{formatCurrency(stats?.referrals?.attributed_revenue)}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Доходы по дням</h3>
          </div>
          <div className="card-content">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.daily_stats || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717a" 
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0a0a0a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value) => [formatCurrency(value), 'Доход']}
                  />
                  <Bar dataKey="amount" fill="#2AABEE" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Transactions Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Количество транзакций</h3>
          </div>
          <div className="card-content">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.daily_stats || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717a" 
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0a0a0a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#00FF94" 
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#00FF94' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Pie Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Распределение подписок</h3>
          </div>
          <div className="card-content">
            <div className="h-64 flex items-center justify-center">
              {subscriptionPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={subscriptionPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {subscriptionPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0a0a0a', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-zinc-500">Нет данных</p>
              )}
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {subscriptionPieData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm text-zinc-400">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Распределение платежей</h3>
          </div>
          <div className="card-content">
            <div className="h-64 flex items-center justify-center">
              {paymentPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {paymentPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0a0a0a', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-zinc-500">Нет данных</p>
              )}
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {paymentPieData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm text-zinc-400">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Revenue by Channel (Monthly) */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <h3 className="card-title">Доход по каналам за последние 30 дней</h3>
          </div>
        </div>
        <div className="card-content">
          {(stats?.revenue_by_channel?.length > 0 || stats?.revenue_by_package?.length > 0) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.revenue_by_channel?.map((item, index) => (
                <div key={item.channel_id} className="p-4 rounded-xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Tv className="w-4 h-4 text-[#2AABEE]" />
                    <span className="text-sm font-medium text-white truncate">{item.channel_name}</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(item.amount)}</p>
                  <p className="text-xs text-zinc-500 mt-1">{item.payments_count} платежей</p>
                </div>
              ))}
              {stats.revenue_by_package?.map((item, index) => (
                <div key={item.package_id} className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-500/[0.02] border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm">📦</span>
                    <span className="text-sm font-medium text-white truncate">{item.package_name}</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-400">{formatCurrency(item.amount)}</p>
                  <p className="text-xs text-zinc-500 mt-1">{item.payments_count} платежей</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-8">Нет данных о доходах за последний месяц</p>
          )}
        </div>
      </div>

      {/* Payments by Method */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#2AABEE]" />
              <h3 className="card-title">Платежи по методам оплаты</h3>
            </div>
          </div>
          <div className="card-content">
            {stats?.payments_by_method?.length > 0 ? (
              <div className="space-y-3">
                {stats.payments_by_method.map((item, index) => (
                  <div key={item.method} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-white">{item.method_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-white">{formatCurrency(item.amount)}</p>
                      <p className="text-xs text-zinc-500">{item.count} платежей</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-4">Нет данных о платежах</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <Tv className="w-5 h-5 text-purple-400" />
              <h3 className="card-title">Подписки по каналам</h3>
            </div>
          </div>
          <div className="card-content">
            {stats?.subscriptions_by_channel?.length > 0 ? (
              <div className="space-y-3">
                {stats.subscriptions_by_channel.map((item, index) => (
                  <div key={item.channel_id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-white">{item.channel_name}</span>
                    </div>
                    <span className="text-sm font-medium text-[#2AABEE]">{item.count} подписчиков</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-4">Нет активных подписок</p>
            )}
            
            {stats?.subscriptions_by_package?.length > 0 && (
              <>
                <div className="border-t border-white/10 my-4" />
                <h4 className="text-sm font-medium text-zinc-400 mb-3">Подписки на пакеты</h4>
                <div className="space-y-3">
                  {stats.subscriptions_by_package.map((item, index) => (
                    <div key={item.package_id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[(index + 4) % COLORS.length] }}
                        />
                        <span className="text-sm text-white">📦 {item.package_name}</span>
                      </div>
                      <span className="text-sm font-medium text-emerald-400">{item.count} подписчиков</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Top Users & Promo Codes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <h3 className="card-title">Топ покупателей</h3>
            </div>
          </div>
          <div className="card-content">
            {stats?.top_users?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_users.map((user, index) => (
                  <div key={user.user_id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-400 text-xs flex items-center justify-center font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <span className="text-sm text-white">
                          {user.username ? `@${user.username}` : `ID: ${user.user_id}`}
                        </span>
                        <p className="text-xs text-zinc-500">{user.payments_count} платежей</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-emerald-400">{formatCurrency(user.total_amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-4">Нет данных о покупателях</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-pink-400" />
              <h3 className="card-title">Использование промокодов</h3>
            </div>
          </div>
          <div className="card-content">
            {stats?.promo_codes_usage?.length > 0 ? (
              <div className="space-y-3">
                {stats.promo_codes_usage.map((promo, index) => (
                  <div key={promo.code} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div>
                        <code className="text-sm text-white bg-white/10 px-2 py-0.5 rounded">{promo.code}</code>
                        <p className="text-xs text-zinc-500 mt-1">{promo.uses} использований</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-pink-400">
                      -{formatCurrency(promo.total_discount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-4">Промокоды пока не использовались</p>
            )}
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2AABEE]/10 flex items-center justify-center">
              <Download className="w-5 h-5 text-[#2AABEE]" />
            </div>
            <div>
              <h3 className="card-title">Экспорт данных</h3>
              <p className="text-xs text-zinc-500">Выгрузка отчетов в CSV или JSON</p>
            </div>
          </div>
        </div>
        <div className="card-content">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger className="w-32 bg-[#121212] border-white/10" data-testid="export-format-select">
                <SelectValue placeholder="Формат" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={handleExportSubscriptions}
              data-testid="export-subscriptions-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Экспорт подписок
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportPayments}
              data-testid="export-payments-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Экспорт платежей
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
